import React from "react";
import { CardContent } from "@/components/ui/card";
import DashboardCardWrapper from "@/components/common/DashboardCardWrapper";
import DashboardCardHeader from "@/components/common/DashboardCardHeader";
import { Typography } from "@/components/ui/typography";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { CardSkeleton } from "@/components/ui/skeleton";
import { TruncatedTextTooltip } from "@/components/common/TextTooltip";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { routes } from "@/utils/urls";
import MedicalIcon from "@/assets/icons/medicalDiagnoses.svg";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";

interface DiagnosesData {
  asthma: boolean;
  cancer: boolean;
  copd: boolean;
  diabetes: boolean;
  chronicKidneyDisease: boolean;
  heartDisease: boolean;
  obesity: boolean;
  arthritis: boolean;
  hypertension: boolean;
  stroke: boolean;
  osteoporosis: boolean;
  hepatitis: boolean;
  hiv: boolean;
  thyroidDisorders: boolean;
  parkinsons: boolean;
  multipleSclerosis: boolean;
}

interface MedicalDiagnosesData {
  memberId: string;
  diagnoses: DiagnosesData;
  loadDate: string;
}

// Diagnosis display names mapping
const diagnosisLabels: Record<keyof DiagnosesData, string> = {
  asthma: "Asthma",
  cancer: "Cancer",
  copd: "COPD",
  diabetes: "Diabetes",
  chronicKidneyDisease: "Chronic Kidney Disease",
  heartDisease: "Heart Disease",
  obesity: "Obesity",
  arthritis: "Arthritis",
  hypertension: "Hypertension",
  stroke: "Stroke",
  osteoporosis: "Osteoporosis",
  hepatitis: "Hepatitis",
  hiv: "HIV/AIDS",
  thyroidDisorders: "Thyroid Disorders",
  parkinsons: "Parkinson's Disease",
  multipleSclerosis: "Multiple Sclerosis",
};

interface MedicalDiagnosesCardProps {
  shouldBlink?: boolean;
}

const MedicalDiagnosesCard: React.FC<MedicalDiagnosesCardProps> = ({
  shouldBlink = false,
}) => {
  const { isFavourite, cardVariant, toggleFavourite } =
    useDashboardCardFavorite("MEDICAL_DIAGNOSES");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: medicalDiagnoses,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<MedicalDiagnosesData>({
    endpoint: routes.cards.medicalDiagnoses,
    memberId,
    processData: (rawData: unknown) =>
      (rawData as { data: MedicalDiagnosesData }).data,
  });

  const getMedicalDiagnoses = () => {
    if (!medicalDiagnoses) return [];

    return Object.entries(medicalDiagnoses.diagnoses) as [
      keyof DiagnosesData,
      boolean,
    ][];
  };

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <div className='text-[#DC2626] dark:text-[#EF4444]'>
                <img
                  src={MedicalIcon}
                  alt='Medical'
                  className='w-4.5 h-4.5 text-[#DC2626] dark:text-[#EF4444]'
                />
              </div>
            </BorderedIconWrapper>
          }
          title='Medical Diagnoses'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />
        <CardContent className='p-4 md:h-[404px] lg:h-[492px] gap-[12px] overflow-hidden'>
          {(() => {
            if (loading) {
              return <CardSkeleton rows={8} showProfile={false} />;
            }

            if (error) {
              return (
                <div className='flex items-center justify-center h-full lg:h-[492px] md:h-[492px]'>
                  <Typography
                    variant='medium-14'
                    className={`${
                      isErrorRed
                        ? "text-red-500 dark:text-red-400"
                        : "text-grey-600 dark:text-grey-500"
                    } font-medium`}>
                    {error}
                  </Typography>
                </div>
              );
            }

            if (!medicalDiagnoses) {
              return null;
            }

            return (
              <div
                className='columns-1 md:columns-2 gap-[10px] h-full'
                style={{ columnFill: "auto" }}>
                {getMedicalDiagnoses().map(([key, value]) => {
                const label = diagnosisLabels[key];
                const isActive = value === true;

                return (
                  <div
                    key={key}
                    className={`
                      flex items-center justify-between gap-1
                      px-4 py-3 rounded-lg mb-[10px]
                      border border-grey-300 dark:border-grey-600
                      break-inside-avoid
                    `}>
                    <TruncatedTextTooltip
                      text={label}
                      className='flex-1 min-w-0'>
                      <Typography
                        variant='medium-14'
                        className={`
                          truncate
                          ${
                            isActive
                              ? "text-grey-700 dark:text-grey-400"
                              : "text-grey-500 dark:text-grey-700"
                          }
                        `}>
                        {label}
                      </Typography>
                    </TruncatedTextTooltip>

                    {isActive ? (
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 20 20'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='flex-shrink-0'>
                        <circle cx='10' cy='10' r='10' fill='#4F5B66' />
                        <path
                          d='M14.5 7L8.5 13L5.5 10'
                          className='stroke-white dark:stroke-black'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <Typography
                        variant='medium-14'
                        className='text-grey-500 dark:text-grey-700 flex-shrink-0'>
                        --
                      </Typography>
                    )}
                  </div>
                );
                })}
              </div>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default MedicalDiagnosesCard;
